/**
 * Typen und Hilfen der Seite „Sicherheitsstrom (ZSV/CPS)" (W2-A2, 16.10.2026).
 *
 * Die Typen folgen `GET …/elektro/sicherheitsstrom` (SicherheitsstromEndpoints.cs,
 * Sicherheitsstromlauf.cs). Gerechnet wird beim Abruf; die Seite schreibt nichts.
 */

export const PDF_HINWEIS =
  'Der PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung mit eigener Dokumentnummer an; sonst liefert er die abgelegte Fassung (F11). '
  + 'Die Excel-Mappe legt keine Fassung an.';

export const ANNAHMEN_HINWEIS =
  'Der Nachweis steht auf unbestätigten Vorgaben: Kapazitätsverhältnis, Wirkungsgrad, Entladeschlussspannung und '
  + 'Ladefaktor sind Planungsannahmen, die Normwerte sind noch nicht gegen den Normtext geprüft. Überschreitungen '
  + 'sind deshalb Warnungen – außer der Mindestbedarf ohne jeden Zuschlag oder die Grenze des Stromkreismoduls ist '
  + 'überschritten: das ist ein Fehler.';

export const ERKENNUNGSWEITE_HINWEIS =
  'Ersatzprüfung, KEIN Beleuchtungsstärkenachweis: geprüft ist nur der Abstand zum nächsten Rettungszeichen gegen '
  + 'd = z · h. 1 lx, 40:1 und die Leuchtdichte brauchen eine Lichtberechnung.';

export interface SsKennzahlen {
  zentralen: number; hauptstationen: number; unterstationen: number; svVerteiler: number; kreise: number;
  leuchten: number; rettungszeichen: number; befunde: number; warnungen: number; fehler: number;
  aufAnnahmen: boolean;
}

export interface SsZentrale {
  kennzeichen: string; hersteller: string | null; baureihe: string | null; rolle: string; haengtAn: string | null;
  lastBetriebW: number | null; lastAnlageW: number | null; batterieart: string | null; akkuAh: number | null;
  ladestromA: number | null; bemessungsleistungZentrale: number | null; bemessungsleistungBaureihe: number | null;
  betriebsdauerH: number | null; umschaltzeitS: number | null; svVerteiler: number; kreise: number;
  leuchten: number; modulkreiseVerfuegbar: number | null; raumnummer: string | null; befunde: string[];
}

export interface SsBatterie {
  station: string; unterstationen: string[]; lastBetriebW: number; lastAnlageW: number; betriebsdauerH: number;
  nennspannungV: number; zellen: number; entladestromA: number; entnommenAh: number; mindestbedarfAh: number;
  kapazitaetErforderlichAh: number; bestandAh: number | null; bestandHerleitung: string | null;
  ladestromErforderlichA: number; ladestromMindestA: number; ladestromA: number | null;
  ladestromHerleitung: string | null; lueftungM3h: number; lueftungQuerschnittCm2: number;
  lueftungKapazitaetAh: number; umschaltzeitS: number | null; umschaltzeitMaxS: number; gegenAnnahmen: boolean;
  status: string; befunde: string[]; rechenweg: string[];
}

export interface SsQuellanlage {
  anlage: string; quelle: string | null; leistungBeleuchtungW: number | null; leistungAnlageW: number | null;
  betriebsdauerH: number | null; kapazitaetAh: number | null; zellen: number | null; ladestromA: number | null;
  lueftungM3h: number | null; lueftungCm2: number | null; nachrechnung: SsBatterie | null; befunde: string[];
}

export interface SsKreis {
  kreis: string; verteiler: string; zentrale: string; laengeM: number | null; querschnittMm2: number | null;
  leitungsart: string | null; leuchten: number; betriebW: number; anschlussW: number; stromBatterieA: number;
  spannungsfallV: number | null; spannungLeuchteV: number | null; spannungsfallZulaessigV: number;
  stromNetzA: number; kreisstromMaxA: number; gegenAnnahmen: boolean; status: string; befunde: string[];
  /** Aus der Kabelbemessung (EK), nur angezeigt. */
  kabeltypBemessung?: string | null; funktionserhaltBemessung?: string | null; stufeBemessung?: string | null;
}

export interface SsZeichen {
  schluessel: string; geschoss: string | null; raumnummer: string | null; abstandM: number | null;
  nachbarSchluessel: string | null; piktogrammhoeheErforderlichM: number | null; ueberschritten: boolean;
}

export interface SsErkennungsweite {
  distanzfaktor: number; piktogrammhoeheM: number; erkennungsweiteM: number; ebenenToleranzM: number;
  zeichen: number; ohneNachbar: number; abstandMedianM: number | null; abstandP95M: number | null;
  abstandMaxM: number | null; ueberschritten: number; befunde: string[]; zeilen: SsZeichen[];
}

export interface SsVorgabe {
  feld: string; verwendung: string | null; wert: string; einheit: string | null;
  bestaetigt: boolean; herkunft: string; normbezug: string | null; rueckfall: boolean;
}

export interface SicherheitsstromStand {
  kennzahlen: SsKennzahlen;
  zentralen: SsZentrale[];
  batterien: SsBatterie[];
  fachplanung: SsQuellanlage[];
  kreise: SsKreis[];
  erkennungsweite: SsErkennungsweite;
  vorgaben: SsVorgabe[];
  befunde: string[];
  rho: number;
  spannungEndeV: number;
  stand: string;
}
